import * as React from "react"
import {
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback"

const SEARCH_DEBOUNCE_MS = 300

function isInputFocused(): boolean {
  const tag = document.activeElement?.tagName
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (document.activeElement as HTMLElement)?.isContentEditable === true
}

export interface FilterOption {
  value: string
  label: string
}

export interface ScriptsFilters {
  topic: string
  concept: string
  presenter: string
  background: string
  sequence: string
  favoritesOnly: string
  /** "all" | "sent" | "notSent" — client-side lens over vendor-send status. */
  sentStatus: string
  /** "all" | "yes" — client-side lens: only scripts with a ready sketch storyboard. */
  hasSketch: string
  /** "all" | "yes" — client-side lens: only scripts with a ready animatic. */
  hasAnimatic: string
}

export interface ScriptsToolbarProps {
  search: string
  onSearchChange: (v: string) => void
  filters: ScriptsFilters
  onFilterChange: (key: keyof ScriptsFilters, value: string) => void
  onClearAll?: () => void
  topicOptions: FilterOption[]
  conceptOptions: FilterOption[]
  presenterOptions: FilterOption[]
  backgroundOptions: FilterOption[]
  sequenceOptions: FilterOption[]
  children?: React.ReactNode
}

/** Checkbox-style filter chip: pressed narrows the list, pressed again clears. */
function FilterToggle({
  active,
  icon,
  activeIconClass,
  label,
  tooltip,
  onToggle,
}: {
  active: boolean
  icon: React.ComponentProps<typeof Icon>["name"]
  /** Extra icon styling while active — e.g. the favorites star fills in. */
  activeIconClass?: string
  label: string
  tooltip?: string
  onToggle: () => void
}) {
  const button = (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={cn(
        "flex h-9 items-center gap-1.5 rounded-md border px-3 text-xs font-medium sm:h-8",
        "transition-colors duration-150",
        active
          ? "border-primary/40 bg-primary/5 text-primary"
          : "border-border/60 bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      <Icon
        name={icon}
        size="xs"
        className={cn(
          "transition-colors duration-150",
          active ? cn("text-primary", activeIconClass) : "text-muted-foreground",
        )}
      />
      {label}
    </button>
  )

  if (!tooltip) return button
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}

function FilterSelect({
  value,
  onValueChange,
  placeholder,
  options,
  tooltip,
}: {
  value: string
  onValueChange: (v: string) => void
  placeholder: string
  options: FilterOption[]
  tooltip?: string
}) {
  const isActive = value !== "all"

  const trigger = (
    <SelectTrigger
      className={`h-9 w-full text-xs transition-colors sm:h-8 sm:w-[160px] ${
        isActive ? "border-primary/40 bg-primary/5 text-foreground" : ""
      }`}
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
  )

  return (
    <Select value={value} onValueChange={onValueChange}>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
      <SelectContent>
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function ScriptsToolbar({
  search,
  onSearchChange,
  filters,
  onFilterChange,
  onClearAll,
  topicOptions,
  conceptOptions,
  presenterOptions,
  backgroundOptions,
  sequenceOptions,
  children,
}: ScriptsToolbarProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = React.useState(search)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const { debounced: debouncedSearch, cancel } = useDebouncedCallback(onSearchChange, SEARCH_DEBOUNCE_MS)

  React.useEffect(() => {
    setInputValue(search)
    cancel()
  }, [search, cancel])

  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "/" && !isInputFocused()) {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  const selectFilterCount = Object.entries(filters).filter(([k, v]) => k !== "favoritesOnly" && v !== "all").length
  const isFavoritesActive = filters.favoritesOnly === "favorites"
  const [filtersOpen, setFiltersOpen] = React.useState(selectFilterCount > 0 || isFavoritesActive)

  React.useEffect(() => {
    if (selectFilterCount > 0 || isFavoritesActive) setFiltersOpen(true)
  }, [selectFilterCount, isFavoritesActive])

  return (
    <div className="flex flex-col gap-2">
      {/* Primary row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full min-w-[160px] sm:w-64">
          <Input
            ref={searchInputRef}
            iconStart="search"
            placeholder={t("opportunities.scripts.toolbar.search")}
            value={inputValue}
            onChange={(e) => {
              const next = e.target.value
              setInputValue(next)
              debouncedSearch(next)
            }}
            className={`h-9 text-sm sm:h-8 ${inputValue ? "pr-8" : "pr-9"}`}
          />
          {!inputValue && (
            <kbd aria-hidden="true" className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline-block">
              /
            </kbd>
          )}
          {inputValue && (
            <button
              type="button"
              onClick={() => { cancel(); setInputValue(""); onSearchChange("") }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t("common.clear")}
            >
              <Icon name="x" size="xs" />
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => setFiltersOpen((prev) => !prev)}
          className={`flex h-9 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors sm:h-8 ${
            selectFilterCount > 0
              ? "border-primary/40 bg-primary/5 text-foreground"
              : "border-border text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
          aria-expanded={filtersOpen}
        >
          <Icon name="filter" size="xs" />
          {t("common.filters")}
          {selectFilterCount > 0 && (
            <span className="flex size-4 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold tabular-nums text-primary">
              {selectFilterCount}
            </span>
          )}
          <Icon name={filtersOpen ? "chevron-up" : "chevron-down"} size="xs" className="ml-0.5 text-muted-foreground/60" />
        </button>

        {selectFilterCount > 0 && onClearAll && (
          <button
            type="button"
            onClick={onClearAll}
            className="flex h-9 items-center gap-1 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:h-8"
          >
            <Icon name="x" size="xs" />
            {t("common.clearFilters")}
          </button>
        )}

        {children && <div className="ml-auto flex items-center gap-2">{children}</div>}
      </div>

      {/* Collapsible filter row */}
      {filtersOpen && (
        <TooltipProvider delayDuration={400}>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center animate-in fade-in slide-in-from-top-1 duration-150">
            <FilterToggle
              active={isFavoritesActive}
              icon="star"
              activeIconClass="fill-primary"
              label={t("opportunities.scripts.toolbar.viewFavorites")}
              onToggle={() => onFilterChange("favoritesOnly", isFavoritesActive ? "all" : "favorites")}
            />
            <FilterToggle
              active={filters.hasSketch === "yes"}
              icon="clapperboard"
              label={t("opportunities.scripts.toolbar.hasSketch", "Sketch")}
              tooltip={t(
                "opportunities.scripts.toolbar.hasSketchTooltip",
                "Only scripts with a sketch storyboard",
              )}
              onToggle={() =>
                onFilterChange("hasSketch", filters.hasSketch === "yes" ? "all" : "yes")
              }
            />
            <FilterToggle
              active={filters.hasAnimatic === "yes"}
              icon="play"
              label={t("opportunities.scripts.toolbar.hasAnimatic", "Animatic")}
              tooltip={t(
                "opportunities.scripts.toolbar.hasAnimaticTooltip",
                "Only scripts with an animatic",
              )}
              onToggle={() =>
                onFilterChange("hasAnimatic", filters.hasAnimatic === "yes" ? "all" : "yes")
              }
            />
            <FilterSelect
              value={filters.sentStatus}
              onValueChange={(v) => onFilterChange("sentStatus", v)}
              placeholder={t("opportunities.scripts.toolbar.anySendStatus")}
              options={[
                { value: "sent", label: t("opportunities.scripts.toolbar.sendStatusSent") },
                { value: "notSent", label: t("opportunities.scripts.toolbar.sendStatusNotSent") },
              ]}
              tooltip={t("opportunities.scripts.toolbar.sendStatusTooltip")}
            />
            <FilterSelect
              value={filters.topic}
              onValueChange={(v) => onFilterChange("topic", v)}
              placeholder={t("opportunities.scripts.toolbar.allTopics")}
              options={topicOptions}
              tooltip={t("opportunities.scripts.toolbar.topicTooltip")}
            />
            <FilterSelect
              value={filters.concept}
              onValueChange={(v) => onFilterChange("concept", v)}
              placeholder={t("opportunities.scripts.toolbar.allConcepts")}
              options={conceptOptions}
              tooltip={t("opportunities.scripts.toolbar.conceptTooltip")}
            />
            <FilterSelect
              value={filters.presenter}
              onValueChange={(v) => onFilterChange("presenter", v)}
              placeholder={t("opportunities.scripts.toolbar.allPresenters")}
              options={presenterOptions}
              tooltip={t("opportunities.scripts.toolbar.presenterTooltip")}
            />
            <FilterSelect
              value={filters.background}
              onValueChange={(v) => onFilterChange("background", v)}
              placeholder={t("opportunities.scripts.toolbar.allBackgrounds")}
              options={backgroundOptions}
              tooltip={t("opportunities.scripts.toolbar.backgroundTooltip")}
            />
            <FilterSelect
              value={filters.sequence}
              onValueChange={(v) => onFilterChange("sequence", v)}
              placeholder={t("opportunities.scripts.toolbar.allSequences")}
              options={sequenceOptions}
              tooltip={t("opportunities.scripts.toolbar.sequenceTooltip")}
            />
          </div>
        </TooltipProvider>
      )}
    </div>
  )
}
